import { useState, useEffect, useContext } from "react";
import { SiteContext } from "../contexts/SiteContext";
import { Link } from "react-router-dom";
import DOMPurify from "dompurify";
import {
  Card,
  CardContent,
  CardMedia,
  Grid,
  Typography,
  Container,
  Fade,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { fetchAll } from "../utils/fetchResources";
import logo from "../inc/lva-drk-logo.svg";
import placeholder from "../inc/placeholder.jpg";
import Loading from "../components/Loading";

const useStyles = makeStyles((theme) => ({
  icon: {
    marginRight: theme.spacing(2),
  },
  main: {
    maxWidth: process.env.REACT_APP_MAX_WIDTH,
    margin: "0 auto",
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  heroContent: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(4, 2, 4),
  },
  cardGrid: {
    marginBottom: "5rem",
  },
  cardWrapper: {
    margin: theme.spacing(1),
  },
  card: {
    textDecoration: "none",
    display: "flex",
    width: "100%",
    minHeight: "250px",
    transition: ".2s",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
    "&:hover": {
      transform: "translateY(-2px)",
    },
  },
  cardMedia: {
    border: "solid .5rem black",
    backgroundColor: "black",
    backgroundSize: "contain",
    width: "33%",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      paddingTop: "56.25%", // 16:9
    },
  },
  cardContent: {
    width: "66%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  footer: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(6),
  },
}));

const ConditionalLink = ({ condition, wrapper, children }) =>
  condition ? wrapper(children) : children;

export default function SiteSelect() {
  const classes = useStyles();
  // Reset site context to empty so it doesn't load weirdly when you click a site
  const { setSelectedSite } = useContext(SiteContext);
  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState();
  const [externalSites, setExternalSites] = useState();
  const [allSites, setAllSites] = useState();

  useEffect(() => {
    async function fetchData() {
      await fetchAll("sites", setSites, "name");
      await fetchAll("externalsites", setExternalSites, "name");
    }
    fetchData();
    setSelectedSite(undefined);
    setLoading(false);
  }, [setSelectedSite]);

  useEffect(() => {
    if (sites && externalSites) {
      // Only show published sites
      const onlyPublished = sites.filter((site) => site.published);
      //Add the externalSites array of objects to the sites state
      const sitesPlusExternalSites = [...onlyPublished, ...externalSites];
      //Set a new allSites state with the alphabetically sorted array of sites objects
      setAllSites(
        sitesPlusExternalSites.sort((a, b) =>
          a.name > b.name ? 1 : b.name > a.name ? -1 : 0
        )
      );
    }
  }, [sites, externalSites]);

  if (loading || !allSites) {
    return <Loading />;
  }

  return (
    <Fade in={!loading}>
      <main className={classes.main}>
        {/* Hero unit */}
        <div className={classes.heroContent}>
          <Container
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
            maxWidth="md"
          >
            <a href="https://www.lva.virginia.gov/collections/educator-resources">
              <img
                src={logo}
                alt="Library of Virginia Logo"
                style={{ width: "200px", marginBottom: "2rem" }}
              />
            </a>
            <Typography
              color="primary"
              component="h1"
              variant="h4"
              align="center"
              gutterBottom
            >
              Select a Site
            </Typography>
            <Typography
              color="textSecondary"
              component="span"
              variant="h6"
              align="center"
              paragraph
            >
              Choose the <i>Online Classroom</i> website you would like to view
              from the list below.
            </Typography>
          </Container>
        </div>
        <Container className={classes.cardGrid} maxWidth="md">
          {/* End hero unit */}
          <Grid container spacing={4}>
            {allSites &&
              allSites.map((site, index) => (
                <ConditionalLink
                  condition={site.url}
                  wrapper={(children) => (
                    <a
                      style={{ textDecoration: "none", width: "100%" }}
                      href={site.url}
                    >
                      {children}
                    </a>
                  )}
                >
                  {/*Above
									 * conditionally render a link around the Card if the site.url exists
									 * the site.url field should only exist in the external site objects
									 */}
                  <Card
                    style={{ marginTop: "1rem" }}
                    className={classes.card}
                    {...(site.slug ? { component: Link } : {})}
                    {...(site.slug ? { to: `/${site.slug}` } : {})}
                  >
                    {/*Above
										 *Conditionally render the Link component and the site slug link
										 *The site.slug should only exist on the objects fetched from the API
										 */}
                    <CardMedia
                      className={classes.cardMedia}
                      image={
                        //if featuredImage exists
                        site.featuredImage
                          ? `${process.env.REACT_APP_FILE_URL}/${site.featuredImage.fileName}`
                          : // else
                          placeholder
                      }
                      title={site.name}
                    />
                    <CardContent className={classes.cardContent}>
                      <Typography
                        gutterBottom
                        variant="h5"
                        component="h2"
                        style={{ marginTop: "1rem" }}
                      >
                        {site.name}
                      </Typography>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(site.description),
                        }}
                      ></div>
                    </CardContent>
                  </Card>
                </ConditionalLink>
              ))}
          </Grid>
        </Container>
      </main>
    </Fade>
  );
}
